import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';

interface TaskPhoto {
  id: number;
  task_id: number;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  uploaded_at: string;
}

interface PhotoUploadProps {
  taskId: number;
  isOpen: boolean;
  onClose: () => void;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({ taskId, isOpen, onClose }) => {
  const [photos, setPhotos] = useState<TaskPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [viewingPhoto, setViewingPhoto] = useState<TaskPhoto | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const fetchPhotos = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tasks/${taskId}/photos`);
      const photoData = response.data;
      if (Array.isArray(photoData)) {
        setPhotos(photoData);
      } else {
        console.warn('Photos API returned non-array data:', photoData);
        setPhotos([]);
      }
    } catch (err) {
      console.error('Failed to fetch photos:', err);
      setPhotos([]);
    }
  }, [taskId]);

  const handleDeletePhoto = async (photoId: number) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/tasks/${taskId}/photos/${photoId}`);
      // Remove photo from local state
      setPhotos(prev => prev.filter(photo => photo.id !== photoId));
    } catch (err: any) {
      console.error('Failed to delete photo:', err);
      setError(err.response?.data?.error || 'Failed to delete photo');
    }
  };

  useEffect(() => {
    if (isOpen && taskId) {
      fetchPhotos();
    }
  }, [isOpen, taskId, fetchPhotos]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Clear previous errors
    setError('');
    setUploadErrors([]);
    setUploadProgress({});

    // Validate all files first
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file, index) => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        errors.push(`${file.name}: Please select an image or video file`);
        return;
      }

      if (file.size > 15 * 1024 * 1024) { // 15MB limit
        errors.push(`${file.name}: File size must be less than 15MB`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setUploadErrors(errors);
      return;
    }

    if (validFiles.length === 0) {
      setError('No valid files to upload');
      return;
    }

    setUploading(true);

    // Initialize progress for all files
    const initialProgress: {[key: string]: number} = {};
    validFiles.forEach(file => {
      initialProgress[file.name] = 0;
    });
    setUploadProgress(initialProgress);

    // Upload files sequentially to avoid race conditions and filename collisions
    const results = [];
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const formData = new FormData();
      formData.append('photo', file);

      try {
        // Update progress to show upload starting
        setUploadProgress(prev => ({ ...prev, [file.name]: 25 }));

        // Add a small delay between uploads to ensure unique timestamps
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        await axios.post(`${API_BASE_URL}/api/tasks/${taskId}/photos`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        results.push({ success: true, filename: file.name });
      } catch (err: any) {
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        results.push({ 
          success: false, 
          filename: file.name, 
          error: err.response?.data?.error || 'Upload failed' 
        });
      }
    }

    try {
      
      // Collect any upload errors
      const uploadErrors = results
        .filter(result => !result.success)
        .map(result => `${result.filename}: ${result.error}`);

      if (uploadErrors.length > 0) {
        setUploadErrors(uploadErrors);
      }

      // Show success message for successful uploads
      const successCount = results.filter(result => result.success).length;
      if (successCount > 0) {
        // Clear the progress after a short delay
        setTimeout(() => {
          setUploadProgress({});
        }, 2000);
        
        fetchPhotos(); // Refresh the photo list
      }

      event.target.value = ''; // Clear the input
    } catch (err: any) {
      setError('Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      // Create a synthetic event to reuse the existing upload logic
      const syntheticEvent = {
        target: { files: droppedFiles, value: '' }
      } as React.ChangeEvent<HTMLInputElement>;
      handleFileUpload(syntheticEvent);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Task Photos</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label className="form-label">Upload Photos & Videos</label>
          
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${isDragging ? '#007bff' : '#e8f4f8'}`,
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center',
              backgroundColor: isDragging ? '#f8f9ff' : '#fafafa',
              transition: 'all 0.3s ease',
              position: 'relative'
            }}
          >
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileUpload}
              disabled={uploading}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            />
            
            <div style={{ pointerEvents: 'none' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📸🎥</div>
              <div style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>
                {isDragging ? 'Drop photos or videos here!' : 'Click or drag photos/videos to upload'}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#666' }}>
                Select multiple photos or videos to upload them all at once (max 15MB each)
              </div>
            </div>
          </div>
          {uploading && <div style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem' }}>Uploading files...</div>}
          
          {Object.keys(uploadProgress).length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              {Object.entries(uploadProgress).map(([filename, progress]) => (
                <div key={filename} style={{ marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.875rem', color: '#666' }}>{filename}</div>
                  <div style={{ 
                    width: '100%', 
                    height: '4px', 
                    backgroundColor: '#e9ecef', 
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${progress}%`,
                      height: '100%',
                      backgroundColor: progress === 100 ? '#28a745' : '#007bff',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {uploadErrors.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              {uploadErrors.map((errorMsg, index) => (
                <div key={index} style={{ color: '#dc3545', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                  {errorMsg}
                </div>
              ))}
            </div>
          )}
        </div>

        {photos && photos.length > 0 && (
          <div>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Uploaded Photos & Videos</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '1rem'
            }}>
              {(photos || []).map((photo) => {
                const isVideo = photo.mime_type.startsWith('video/');
                return (
                  <div key={photo.id} style={{
                    border: '2px solid #e8f4f8',
                    borderRadius: '8px',
                    padding: '0.5rem',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: 'rgba(220, 53, 69, 0.8)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => (e.target as HTMLButtonElement).style.background = 'rgba(220, 53, 69, 1)'}
                      onMouseLeave={(e) => (e.target as HTMLButtonElement).style.background = 'rgba(220, 53, 69, 0.8)'}
                      title={isVideo ? "Delete video" : "Delete photo"}
                    >
                      ×
                    </button>
                    {isVideo ? (
                      <div
                        onClick={() => setViewingPhoto(photo)}
                        style={{
                          width: '100%',
                          height: '100px',
                          backgroundColor: '#000',
                          borderRadius: '4px',
                          marginBottom: '0.5rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                        <video
                          src={`${API_BASE_URL}/uploads/${photo.filename}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '2rem',
                          color: 'white',
                          textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                        }}>
                          ▶
                        </div>
                      </div>
                    ) : (
                      <img
                        src={`${API_BASE_URL}/uploads/${photo.filename}`}
                        alt={photo.original_filename}
                        onClick={() => setViewingPhoto(photo)}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          marginBottom: '0.5rem',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => (e.target as HTMLImageElement).style.opacity = '0.8'}
                        onMouseLeave={(e) => (e.target as HTMLImageElement).style.opacity = '1'}
                      />
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#666', wordBreak: 'break-word' }}>
                      {isVideo && '🎥 '}
                      {photo.original_filename}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#999' }}>
                      {Math.round(photo.file_size / 1024)}KB
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(!photos || photos.length === 0) && !uploading && (
          <div style={{
            textAlign: 'center',
            color: '#666',
            padding: '2rem',
            border: '2px dashed #e8f4f8',
            borderRadius: '8px',
            marginTop: '1rem'
          }}>
            No photos or videos uploaded yet
          </div>
        )}

        <div style={{ marginTop: '2rem', textAlign: 'right' }}>
          <button onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>

      {viewingPhoto && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={() => setViewingPhoto(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            {viewingPhoto.mime_type.startsWith('video/') ? (
              <video
                src={`${API_BASE_URL}/uploads/${viewingPhoto.filename}`}
                controls
                autoPlay
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain'
                }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={`${API_BASE_URL}/uploads/${viewingPhoto.filename}`}
                alt={viewingPhoto.original_filename}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <button
              onClick={() => setViewingPhoto(null)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
            <div
              style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '10px',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {viewingPhoto.mime_type.startsWith('video/') && '🎥 '}
              {viewingPhoto.original_filename} ({Math.round(viewingPhoto.file_size / 1024)}KB)
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;